import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

import { formatUsd } from '@/lib/format';
import { useAccountCredits, useGroupCredits } from '@/hooks/useCredits';

/**
 * Open invoices − credits on account = balance due.
 *
 * The portal totalled open invoice balances and called that the balance, which
 * is not what QuickBooks calls the balance: QuickBooks nets unapplied payments
 * and credit memos against it. THE MELT MAIN read $9,918.00 here against
 * $2,792.70 there, and nothing on screen explained the $7,125.30 — so a chain
 * paying the portal's number would have overpaid by exactly the credit they
 * had already sent us.
 *
 * Renders nothing when there are no credits: on the ~99% of accounts with none,
 * "less credits: $0.00" is noise that makes the real case harder to notice.
 */
export default function BalanceWithCredits({ openBalance }: { openBalance: number }) {
  const group = useGroupCredits();
  const account = useAccountCredits();

  const isGroup = group.data?.is_group === true;
  const credits = isGroup
    ? (group.data?.totals?.credit_total ?? 0)
    : (account.data ?? []).reduce((s, c) => s + c.creditAmount, 0);
  const count = isGroup ? (group.data?.totals?.credit_count ?? 0) : (account.data ?? []).length;

  if (credits <= 0) return null;

  // Credits can exceed what is open — that is a real state (money on account,
  // nothing outstanding), and it must read as zero owed rather than a negative.
  const due = Math.max(0, Math.round((openBalance - credits) * 100) / 100);
  const inCredit = credits > openBalance;

  return (
    <div className="mb-6 rounded-2xl border-2 border-emerald-300/80 bg-emerald-50/60 px-5 py-4">
      <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-3">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 font-mono text-sm tabular-nums text-brix-ink">
          <span>
            <span className="text-brix-muted">Open invoices</span> {formatUsd(openBalance)}
          </span>
          <span className="text-brix-muted">−</span>
          <span>
            <span className="text-brix-muted">credits on account</span>{' '}
            <span className="font-bold text-emerald-700">{formatUsd(credits)}</span>
          </span>
          <span className="text-brix-muted">=</span>
          <span className="text-base font-bold">
            <span className="font-sans text-sm font-semibold text-brix-muted">balance due </span>
            {formatUsd(due)}
          </span>
        </div>
        <Link
          to="/payments"
          className="inline-flex items-center gap-1 rounded-full border border-emerald-600/40 px-3 py-1.5 text-xs font-semibold tracking-tight text-emerald-800 transition-colors hover:bg-emerald-100"
        >
          See the {count} {count === 1 ? 'credit' : 'credits'} <ArrowRight size={13} />
        </Link>
      </div>
      {inCredit && (
        <p className="mt-2 text-xs font-semibold text-emerald-800">
          You&apos;re in credit — we hold more than the invoices currently open.
        </p>
      )}
    </div>
  );
}
